/**
 * Client-Side Geo-Blocking
 * 
 * Defense-in-depth layer that blocks users from high-risk countries
 * BEFORE authenticated UI renders. This prevents:
 * - Information disclosure about app structure
 * - Probing of admin/dealer interfaces
 * - Wasted bandwidth on blocked requests
 * 
 * Note: This is a client-side check. True edge blocking requires
 * Cloudflare/Vercel WAF configuration (see docs/GEO_BLOCKING_CONFIG.md)
 */

/** Countries blocked from accessing protected routes */
export const BLOCKED_COUNTRIES = new Set([
  'CN', // China
  'RU', // Russia
  'KP', // North Korea
  'IR', // Iran
  'BY', // Belarus
  'VE', // Venezuela
  'CU', // Cuba
  'SY', // Syria
]);

/** Routes that require geo-blocking */
export const GEO_BLOCKED_ROUTES = [
  '/admin',
  '/dealer',
  '/auth',
  '/account',
];

export interface GeoCheckResult {
  blocked: boolean;
  country: string | null;
  reason?: string;
}

/**
 * Check if the current user's country is blocked
 * Uses a lightweight geo-detection endpoint
 */
export async function checkGeoBlock(): Promise<GeoCheckResult> {
  try {
    // Try to detect country from Cloudflare/Vercel headers
    // This endpoint returns the country code based on request origin
    const response = await fetch('/api/geo-check', {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    });

    if (!response.ok) {
      // If geo-check fails, check localStorage cache
      const cachedCountry = localStorage.getItem('_geo_country');
      if (cachedCountry && BLOCKED_COUNTRIES.has(cachedCountry)) {
        return {
          blocked: true,
          country: cachedCountry,
          reason: 'Service not available in your region',
        };
      }
      // Fail open if we can't determine country
      return { blocked: false, country: null };
    }

    const data = await response.json();
    const country = (data.country || '').toUpperCase();

    // Cache for subsequent checks
    if (country) {
      localStorage.setItem('_geo_country', country);
    }

    if (BLOCKED_COUNTRIES.has(country)) {
      return {
        blocked: true,
        country,
        reason: 'Service not available in your region',
      };
    }

    return { blocked: false, country };
  } catch (error) {
    console.warn('Geo-check failed, allowing access:', error);
    // Fail open on error
    return { blocked: false, country: null };
  }
}

/**
 * Synchronous check using cached country (for initial render)
 */
export function checkGeoBlockSync(): GeoCheckResult {
  const cachedCountry = localStorage.getItem('_geo_country');
  
  if (cachedCountry && BLOCKED_COUNTRIES.has(cachedCountry)) {
    return {
      blocked: true,
      country: cachedCountry,
      reason: 'Service not available in your region',
    };
  }

  return { blocked: false, country: cachedCountry };
}

/**
 * Check if current path requires geo-blocking
 */
export function isGeoBlockedRoute(pathname: string): boolean {
  return GEO_BLOCKED_ROUTES.some(route => pathname.startsWith(route));
}
